<template>
  <div
    class="form-container"
  >
    <h2>{{ $localize('EditCameraTitle') }}</h2>
    <CameraEditForm
      ref="editForm"
      v-model:camera-name="cameraModel.name"
      v-model:internal-name="cameraModel.internalName"
      v-model:camera-make="cameraModel.make"
      v-model:camera-model="cameraModel.model"
      v-model:camera-notes="cameraModel.notes"
      v-model:camera-coordinate="cameraModel.coordinate"
      :server-errors="serverErrors"
      :validation-rules="camera?.validationRules"
      :hide-map-marker="loadingPage"
    />

    <div class="row">
      <div class="col-xs-12 text-right col-xs-margin-top">
        <VueButton
          :type="VueButtonTypes.default"
          :disabled="saving"
          :loading="deleting"
          @click="showDeleteModalDialog"
        >
          {{ $localize('DeleteCamera') }}
        </VueButton>

        <VueButton
          :type="VueButtonTypes.primary"
          :loading="saving"
          :disabled="deleting"
          class="ml-1"
          aria-describedby="cameraNameValidationMessage internalNameValidationMessage cameraMakeValidationMessage
          cameraModelValidationMessage cameraDescriptionValidationMessage serverErrorValidationMessages"
          @click="saveCamera"
        >
          {{ $localize('SaveCamera') }}
        </VueButton>
      </div>
    </div>
  </div>

  <ModalDialog
    v-if="showDeleteWarningModal"
    :is-danger="true"
    :title="$localize('CameraDeleteModalTitle')"
    @close="closeDeleteModalDialog"
  >
    <template #default>
      <div>{{ $localize('CameraDeleteModalMessage') }}</div>
      <br>
      <div>{{ $localize('CameraDeleteModalQuestion') }}</div>
    </template>
    <template #footer>
      <VueButton
        :type="VueButtonTypes.default"
        @click="closeDeleteModalDialog"
      >
        {{ $localize('Cancel') }}
      </VueButton>
      <VueButton
        :type="VueButtonTypes.danger"
        @click="deleteCamera"
      >
        {{ $localize('Continue') }}
      </VueButton>
    </template>
  </ModalDialog>
</template>

<script lang="ts" setup>
import { reactive, ref } from 'vue';
import { ApplicationError } from '@/Services/Clients/applicationError';
import CameraEditForm from '@/VueComponents/Cameras/CameraDetails/CameraEditForm.vue';
import cameraService from '@/Services/cameraService';
import logger from '@/Utils/logger';
import { useRouter } from 'vue-router';
import VueButton from '@/VueComponents/SharedComponents/Buttons/VueButton.vue';
import { VueButtonTypes } from '@/VueComponents/SharedComponents/Buttons/Enums/VueButtonTypes';
import ModalDialog from '@/VueComponents/Modals/ModalDialog.vue';
import usePageLoading from '@/VueCore/utils/usePageLoading';

const router = useRouter();

const cameraModel = reactive({
  name: '',
  internalName: '',
  make: '',
  model: '',
  notes: '',
  coordinate: {
    longitude: 0,
    latitude: 0
  }
});

const camera = ref(null);

const { loadingPage } = usePageLoading(async routeParams => {
  camera.value = await cameraService.getCamera(routeParams.id as string);

  cameraModel.name = camera.value.cameraName;
  cameraModel.internalName = camera.value.internalName;
  cameraModel.make = camera.value.cameraMake;
  cameraModel.model = camera.value.cameraModel;
  cameraModel.notes = camera.value.description,
  cameraModel.coordinate = {
    longitude: camera.value.coordinate.longitude,
    latitude: camera.value.coordinate.latitude
  };
}, error => console.error('Failed to fetch camera details:', error));

const editForm = ref(null);
const saving = ref(false);
const deleting = ref(false);
const serverErrors = ref([]);

const showDeleteWarningModal = ref(false);

async function saveCamera() {
  if (!(await editForm.value.isValid())) {
    return;
  }

  saving.value = true;
  serverErrors.value = [];

  const request = {
    businessId: camera.value.businessId,
    cameraId: camera.value.cameraId,
    rowversion: camera.value.rowversion,
    cameraName: cameraModel.name,
    internalName: cameraModel.internalName,
    cameraMake: cameraModel.make,
    cameraModel: cameraModel.model,
    description: cameraModel.notes,
    coordinate: cameraModel.coordinate
  };

  cameraService.updateCamera(request)
      .then(function () {

        logger.success('CameraUpdatedSummary');

        router.push('/cameras');
      })
      .catch((error:ApplicationError) => {
        saving.value = false;

        if (error?.errorData?.errorMessages?.length) {
          serverErrors.value = error.errorData.errorMessages;
          return;
        }

        if (!error.isLogged) {
          logger.error('UnhandledError', 'An unexpected error occurred while attempting to update the camera.', error);
        }
      });
}

function deleteCamera() {
  showDeleteWarningModal.value = false;

  deleting.value = true;
  serverErrors.value = [];

  cameraService.deleteCamera(camera.value.cameraId)
      .then(function () {
        logger.success('CameraDeletedSummary');
        router.push('/cameras');
      })
      .catch((error: ApplicationError) => {
        deleting.value = false;

        if (error?.errorData?.errorMessages?.length) {
          serverErrors.value = error.errorData.errorMessages;
          return;
        }

        if (!error.isLogged) {
          logger.error('UnexpectedErrorWhileDeletingCamera', null, error);
        }
      });
}

function showDeleteModalDialog() {
  showDeleteWarningModal.value = true;
}

function closeDeleteModalDialog() {
  showDeleteWarningModal.value = false;
}
</script>
